import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Plus, Rocket, Pencil, Trash2 } from "lucide-react";
import { fetchClient, getCampaigns, launchCampaign, deleteCampaign } from "@/lib/api";
import type { Campaign, SerializedIntegration } from "@/types";
import { toast } from "sonner";
import CampaignForm from "@/components/CampaignForm";

export const Route = createFileRoute("/dashboard/clients/$id/integrations/$integrationId/campaigns")({
  component: CampaignsPage,
});

function CampaignsPage() {
  const { id: businessId, integrationId } = Route.useParams();
  const navigate = useNavigate();
  const [integration, setIntegration] = useState<SerializedIntegration | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isLaunching, setIsLaunching] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch client data to get integration details
      const clientData = await fetchClient(businessId);
      const foundIntegration = clientData.integrations?.find((i) => i.id === integrationId);

      if (!foundIntegration) {
        toast.error("Integration not found");
        navigate({ to: "/dashboard/clients/$id", params: { id: businessId } });
        return;
      }

      setIntegration(foundIntegration);

      // Fetch campaigns
      const campaignsData = await getCampaigns(integrationId);
      setCampaigns(campaignsData.campaigns);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [businessId, integrationId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLaunch = async (campaignId: string) => {
    setIsLaunching(campaignId);
    try {
      const result = await launchCampaign(campaignId);

      if (result.success) {
        toast.success("Campaign launched successfully!", {
          description: `${result.contactsAdded || 0} contacts added to the campaign`,
        });
        await loadData();
      } else {
        toast.error("Failed to launch campaign", {
          description: result.error,
        });
      }
    } catch (error) {
      console.error("Error launching campaign:", error);
      toast.error("An error occurred while launching the campaign");
    } finally {
      setIsLaunching(null);
    }
  };

  const handleDelete = async (campaignId: string) => {
    setIsDeleting(true);
    try {
      const result = await deleteCampaign(campaignId);

      if (result.success) {
        toast.success("Campaign deleted successfully");
        await loadData();
        setShowDeleteConfirm(null);
      } else {
        toast.error("Failed to delete campaign", {
          description: result.error,
        });
      }
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error("An error occurred while deleting the campaign");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setShowForm(true);
  };

  const handleCreateNew = () => {
    setEditingCampaign(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingCampaign(null);
  };

  const handleFormSuccess = async () => {
    await loadData();
    handleFormClose();
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "ready":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPublishedBadgeColor = (published: boolean) => {
    return published ? "bg-purple-100 text-purple-800" : "bg-gray-100 text-gray-600";
  };

  // Find campaigns by status
  const liveCampaign = campaigns.find((c) => c.status === "active");
  const readyToLaunchCampaigns = campaigns.filter((c) => c.published && c.status === "ready");

  if (loading || !integration) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (showDeleteConfirm) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <Link to="/dashboard/clients/$id" params={{ id: businessId }}>
              <Button variant="ghost" size="sm" className="mb-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Client
              </Button>
            </Link>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Delete Campaign?</CardTitle>
              <CardDescription>This will permanently delete the campaign and all associated data.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(null)} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={() => handleDelete(showDeleteConfirm)} disabled={isDeleting}>
                  {isDeleting ? "Deleting..." : "Delete Campaign"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <Button variant="ghost" size="sm" onClick={handleFormClose} className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Campaigns
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{editingCampaign ? "Edit Campaign" : "Create New Campaign"}</CardTitle>
              <CardDescription>
                {editingCampaign ? "Update campaign details and content" : "Create a new campaign for this integration"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CampaignForm
                integrationId={integrationId}
                campaign={editingCampaign}
                onSuccess={handleFormSuccess}
                onCancel={handleFormClose}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <Link to="/dashboard/clients/$id" params={{ id: businessId }}>
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Client
            </Button>
          </Link>
        </div>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{integration.name}</h1>
              <p className="text-muted-foreground mt-1">Manage campaigns for this integration</p>
            </div>
            <Button onClick={handleCreateNew}>
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </div>
        </div>

        {/* Live Campaign Section */}
        {liveCampaign && (
          <Card className="mb-6 border-green-600 bg-green-50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center text-slate-gray gap-2">
                    {liveCampaign.name}
                    <span className={`text-xs px-2 py-1 rounded ${getStatusBadgeColor("active")}`}>Live</span>
                    <span className={`text-xs px-2 py-1 rounded ${getPublishedBadgeColor(true)}`}>Published</span>
                  </CardTitle>
                  <CardDescription className="mt-2">Tags: {liveCampaign.audienceTags.join(", ")}</CardDescription>
                  {liveCampaign.matchingContactsCount !== undefined && (
                    <CardDescription className="mt-1 font-medium text-slate-gray">
                      {liveCampaign.matchingContactsCount} contact{liveCampaign.matchingContactsCount !== 1 ? "s" : ""}{" "}
                      match{liveCampaign.matchingContactsCount !== 1 ? "" : "es"} audience tags
                    </CardDescription>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div>
                {/* Campaign Stats */}
                {liveCampaign.stats && (
                  <div className="grid grid-cols-5 gap-4">
                    <div className="p-3 bg-white rounded-md border">
                      <p className="text-sm text-muted-foreground">Contacts Added</p>
                      <p className="text-2xl text-green-600/60 font-bold">{liveCampaign.stats.contactsAdded}</p>
                    </div>
                    <div className="p-3 bg-white rounded-md border">
                      <p className="text-sm text-muted-foreground">Emails Sent</p>
                      <p className="text-2xl text-green-600/60 font-bold">{liveCampaign.stats.emailsSent}</p>
                    </div>
                    <div className="p-3 bg-white rounded-md border">
                      <p className="text-sm text-muted-foreground">SMS Sent</p>
                      <p className="text-2xl text-green-600/60 font-bold">{liveCampaign.stats.smsSent}</p>
                    </div>
                    <div className="p-3 bg-white rounded-md border">
                      <p className="text-sm text-muted-foreground">Replies</p>
                      <p className="text-2xl text-green-600/60 font-bold">{liveCampaign.stats.replies}</p>
                    </div>
                    <div className="p-3 bg-white rounded-md border">
                      <p className="text-sm text-muted-foreground">Finished</p>
                      <p className="text-2xl text-green-600/60 font-bold">{liveCampaign.stats.finished}</p>
                    </div>
                  </div>
                )}

                {/* Campaign Content Preview */}
                {liveCampaign.content && Object.keys(liveCampaign.content).length > 0 && (
                  <div className="mt-8">
                    <p className="text-sm font-medium text-slate-gray mb-3">Campaign Content</p>
                    <div>
                      {Object.entries(liveCampaign.content).map(([variableName, value]) => (
                        <div key={variableName} className="p-3 bg-white rounded-md border mb-2">
                          <p className="text-sm font-medium text-slate-gray mb-2">{variableName}</p>
                          <p className="text-xs text-muted-foreground line-clamp-4">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ready to Launch Section */}
        {readyToLaunchCampaigns.length > 0 && (
          <Card className="mb-6 border-off-white bg-soft-blue/80">
            <CardHeader>
              <CardTitle className="text-background">Ready to Launch</CardTitle>
              <CardDescription className="text-muted">
                Published campaigns that are ready to be launched
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {readyToLaunchCampaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-start justify-between p-4 bg-off-white border rounded-md"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-background">{campaign.name}</p>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusBadgeColor(campaign.status)}`}>
                          {campaign.status}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${getPublishedBadgeColor(true)}`}>Published</span>
                      </div>
                      <p className="text-sm text-muted-foreground">Tags: {campaign.audienceTags.join(", ")}</p>
                      {campaign.matchingContactsCount !== undefined && (
                        <p className="text-sm font-medium text-slate-gray mt-1">
                          {campaign.matchingContactsCount} contact{campaign.matchingContactsCount !== 1 ? "s" : ""} will
                          be added to workflow
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleLaunch(campaign.id)}
                        disabled={isLaunching === campaign.id}
                        className="border border-green-600 bg-green-600/20 text-green-600 hover:bg-green-600/10"
                      >
                        <Rocket className="w-4 h-4 mr-1" />
                        {isLaunching === campaign.id ? "Launching..." : "Launch"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(campaign)}
                        disabled={isLaunching !== null}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteConfirm(campaign.id)}
                        disabled={isLaunching !== null}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Campaigns List */}
        <Card>
          <CardHeader>
            <CardTitle>All Campaigns ({campaigns.length})</CardTitle>
            <CardDescription>
              {campaigns.length === 0
                ? "No campaigns yet. Create your first campaign to get started."
                : "Manage all campaigns for this integration"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No campaigns have been created yet.</p>
                <Button onClick={handleCreateNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Campaign
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-start justify-between p-4 border rounded-md bg-accent/10 hover:bg-accent/20 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-foreground">{campaign.name}</p>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusBadgeColor(campaign.status)}`}>
                          {campaign.status}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${getPublishedBadgeColor(campaign.published)}`}>
                          {campaign.published ? "Published" : "Draft"}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">Tags: {campaign.audienceTags.join(", ")}</p>
                      {campaign.matchingContactsCount !== undefined && (
                        <p className="text-sm font-medium text-slate-gray">
                          {campaign.matchingContactsCount} contact{campaign.matchingContactsCount !== 1 ? "s" : ""}{" "}
                          match{campaign.matchingContactsCount !== 1 ? "" : "es"} audience tags
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(campaign)}
                        disabled={isLaunching !== null}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteConfirm(campaign.id)}
                        disabled={isLaunching !== null}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
